import { create } from 'zustand';
import type { User, Notification, Vessel, Inspection, CorrectiveAction, Observation, ColorCode, JobClassification } from '../types';
import { currentUser, mockNotifications, mockVessels, mockInspections, mockCorrectiveActions, mockObservations } from '../data/mockData';

interface AppState {
  // Auth
  currentUser: User | null;
  isAuthenticated: boolean;
  login: (email: string, password: string) => boolean;
  logout: () => void;

  // UI
  sidebarOpen: boolean;
  sidebarCollapsed: boolean;
  toggleSidebar: () => void;
  toggleSidebarCollapse: () => void;
  setSidebarOpen: (open: boolean) => void;

  // Notifications
  notifications: Notification[];
  unreadCount: number;
  markNotificationRead: (id: string) => void;
  markAllRead: () => void;

  // Vessels
  vessels: Vessel[];
  selectedVessel: Vessel | null;
  setSelectedVessel: (vessel: Vessel | null) => void;
  addVessel: (vessel: Omit<Vessel, 'id' | 'totalInspections' | 'openFindings' | 'overdueActions' | 'certificates'>) => void;

  // Inspections
  inspections: Inspection[];
  selectedInspection: Inspection | null;
  setSelectedInspection: (inspection: Inspection | null) => void;

  // Corrective Actions
  correctiveActions: CorrectiveAction[];
  updateCAPAStatus: (id: string, status: CorrectiveAction['status']) => void;
  updateCAPAColorCode: (id: string, colorCode: ColorCode) => void;
  updateCAPACloseout: (id: string, closeoutComments: string) => void;

  // Observations
  observations: Observation[];

  // Search
  globalSearch: string;
  setGlobalSearch: (query: string) => void;
  searchOpen: boolean;
  setSearchOpen: (open: boolean) => void;
}

export const useAppStore = create<AppState>((set, get) => ({
  // Auth
  currentUser: currentUser,
  isAuthenticated: false,
  login: (email: string, password: string) => {
    if (email === 'a@a1.com' && password === 'a123#') {
      set({ currentUser: currentUser, isAuthenticated: true });
      return true;
    }
    return false;
  },
  logout: () => set({ currentUser: null, isAuthenticated: false }),

  // UI
  sidebarOpen: true,
  sidebarCollapsed: false,
  toggleSidebar: () => set(state => ({ sidebarOpen: !state.sidebarOpen })),
  toggleSidebarCollapse: () => set(state => ({ sidebarCollapsed: !state.sidebarCollapsed })),
  setSidebarOpen: (open: boolean) => set({ sidebarOpen: open }),

  // Notifications
  notifications: mockNotifications,
  unreadCount: mockNotifications.filter(n => !n.isRead).length,
  markNotificationRead: (id: string) => set(state => {
    const updated = state.notifications.map(n => n.id === id ? { ...n, isRead: true } : n);
    return { notifications: updated, unreadCount: updated.filter(n => !n.isRead).length };
  }),
  markAllRead: () => set(state => ({
    notifications: state.notifications.map(n => ({ ...n, isRead: true })),
    unreadCount: 0,
  })),

  // Vessels
  vessels: mockVessels,
  selectedVessel: null,
  setSelectedVessel: (vessel) => set({ selectedVessel: vessel }),
  addVessel: (vesselData) => set(state => {
    const newId = `ves-${String(state.vessels.length + 1).padStart(3, '0')}`;
    const newVessel: Vessel = {
      ...vesselData,
      id: newId,
      totalInspections: 0,
      openFindings: 0,
      overdueActions: 0,
      certificates: [],
    };
    return { vessels: [...state.vessels, newVessel] };
  }),

  // Inspections
  inspections: mockInspections,
  selectedInspection: null,
  setSelectedInspection: (inspection) => set({ selectedInspection: inspection }),

  // Corrective Actions
  correctiveActions: mockCorrectiveActions,
  updateCAPAStatus: (id, status) => set(state => ({
    correctiveActions: state.correctiveActions.map(c => c.id === id ? { ...c, status, updatedAt: new Date().toISOString() } : c)
  })),
  updateCAPAColorCode: (id, colorCode) => set(state => ({
    correctiveActions: state.correctiveActions.map(c => c.id === id ? { ...c, colorCode, updatedAt: new Date().toISOString() } : c)
  })),
  updateCAPACloseout: (id, closeoutComments) => set(state => ({
    correctiveActions: state.correctiveActions.map(c => c.id === id ? { ...c, closeoutComments, closeoutDate: new Date().toISOString(), updatedAt: new Date().toISOString() } : c)
  })),

  // Observations
  observations: mockObservations,

  // Search
  globalSearch: '',
  setGlobalSearch: (query: string) => set({ globalSearch: query }),
  searchOpen: false,
  setSearchOpen: (open: boolean) => set({ searchOpen: open }),
}));
